/**
 * CallDigestsPanel — review AI-generated, personalised funding-call emails
 * (max 5 calls per user). Drafts are emailed only when approved here.
 */
import React, { useState, useMemo, useEffect } from 'react';
import useSWR from 'swr';
import { Link as RouterLink } from 'react-router-dom';
import {
  Paper, Typography, Box, Chip, CircularProgress, Alert, Button, Checkbox, Stack, Tooltip,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Link, IconButton,
  ToggleButtonGroup, ToggleButton, MenuItem, TextField, Divider, Autocomplete, FormControlLabel, Switch,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon, Send as SendIcon, Cancel as RejectIcon,
  Visibility as PreviewIcon, Refresh as RegenerateIcon, Close as RemoveIcon, DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import EmailOutboxDialog from './email/EmailOutboxDialog';
import { useUserContext } from 'shared/contexts/UserContext';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—');
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const STATUS_COLORS = { draft: 'warning', sent: 'success', rejected: 'default', failed: 'error' };

function DigestCard({ d, selected, onToggle, onAction, busy }) {
  const isDraft = d.status === 'draft';
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: selected ? 'primary.main' : 'divider' }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        {isDraft && <Checkbox checked={selected} onChange={onToggle} sx={{ mt: -0.5 }} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to={`/profile/${d.user.id}/`} target="_blank" fontWeight={700}>
              {d.user.name || d.user.email}
            </Link>
            <Typography variant="body2" color="text.secondary">{d.user.email}</Typography>
            {d.user.position && <Typography variant="caption" color="text.secondary">· {d.user.position}</Typography>}
            {d.user.email_off && (
              <Tooltip title="This person switched email notifications off; it was included by an admin override">
                <Chip size="small" color="warning" variant="outlined" label="Email notifications off" />
              </Tooltip>
            )}
            <Chip size="small" label={d.status} color={STATUS_COLORS[d.status]} sx={{ ml: 'auto' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
            {d.user.research_areas.map((a) => <Chip key={a} size="small" color="secondary" variant="outlined" label={a} />)}
            {d.user.skills.map((s) => <Chip key={s} size="small" variant="outlined" label={s} />)}
          </Box>

          <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic' }}>{d.intro}</Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {d.items.map((it) => (
              <Box key={it.call_id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', pl: 1.5, borderLeft: 3, borderColor: 'primary.light' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Link component={RouterLink} to={`/funding/call/${it.call_id}/`} target="_blank" variant="body2" fontWeight={600}>
                    {it.title}
                  </Link>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {it.code} · {it.program} · deadline {fmtDate(it.closes_at)}
                  </Typography>
                  {it.reason && <Typography variant="body2" sx={{ mt: 0.25 }}><b>Why:</b> {it.reason}</Typography>}
                </Box>
                {isDraft && d.items.length > 1 && (
                  <Tooltip title="Remove this call from the email">
                    <IconButton size="small" disabled={busy} onClick={() => onAction('remove', it.call_id)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Stack>

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button size="small" startIcon={<PreviewIcon />} onClick={() => onAction('preview')}>Preview email</Button>
            {isDraft && (
              <>
                <Button size="small" startIcon={<RegenerateIcon />} disabled={busy} onClick={() => onAction('regenerate')}>Regenerate</Button>
                <Box sx={{ flex: 1 }} />
                <Button size="small" color="error" startIcon={<RejectIcon />} disabled={busy} onClick={() => onAction('reject')}>Reject</Button>
                <Button size="small" variant="contained" color="success" startIcon={<SendIcon />} disabled={busy} onClick={() => onAction('approve')}>
                  Approve &amp; send
                </Button>
              </>
            )}
            {d.status === 'sent' && <Typography variant="caption" color="text.secondary">Sent {fmt(d.sent_at)} by {d.reviewed_by_name}</Typography>}
            {d.status === 'failed' && <Typography variant="caption" color="error">{d.error}</Typography>}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

/** Search people to hand-pick; those who can't receive call emails are shown but can't be chosen. */
function PeoplePicker({ value, onChange, disabled, includeEmailOff }) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const term = input.trim();
    if (term.length < 2) { setOptions([]); return undefined; }
    let live = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axiosService.get('/funding/call-digests/candidates/', { params: { search: term } });
        if (live) setOptions(r.data.results || []);
      } catch { if (live) setOptions([]); } finally { if (live) setLoading(false); }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [input]);
  return (
    <Autocomplete
      multiple
      size="small"
      sx={{ flex: 1, minWidth: 280 }}
      disabled={disabled}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={input}
      onInputChange={(_, v, reason) => { if (reason !== 'reset') setInput(v); }}
      options={options}
      filterOptions={(x) => x}
      loading={loading}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionDisabled={(o) => !o.eligible && !(includeEmailOff && o.overridable)}
      noOptionsText={input.trim().length < 2 ? 'Type a name or email' : 'No one found'}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {o.email}{o.position ? ` · ${o.position}` : ''}
            </Typography>
            {!o.eligible && (includeEmailOff && o.overridable
              ? <Typography variant="caption" color="warning.main">{o.reason}: will be included (admin override)</Typography>
              : <Typography variant="caption" color="error">{o.reason}</Typography>)}
            {o.eligible && o.sparse_profile && (
              <Typography variant="caption" color="text.secondary">No skills or research areas yet: picks use the rest of the profile</Typography>
            )}
          </Box>
        </li>
      )}
      renderTags={(tags, getTagProps) => tags.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
      renderInput={(params) => <TextField {...params} label="Hand-pick people" placeholder={value.length ? '' : 'Search by name or email'} />}
    />
  );
}

export default function CallDigestsPanel() {
  const [status, setStatus] = useState('draft');
  const [batch, setBatch] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);       // { decision, ids? , all? }
  const [preview, setPreview] = useState(null);       // html string
  const [sendReview, setSendReview] = useState(null); // { ids } — drafts to review before sending
  const [toast, setToast] = useState(null);
  const [generating, setGenerating] = useState(null);   // batch id being generated
  const [picked, setPicked] = useState([]);              // hand-picked people for a targeted run
  const [includeEmailOff, setIncludeEmailOff] = useState(false);   // rare superuser override: email notifications off
  const [overrideReason, setOverrideReason] = useState('');
  const { user: me } = useUserContext();

  const { data: batches, mutate: mutateBatches } = useSWR('/funding/call-digests/batches/', fetcher,
    { refreshInterval: generating ? 4000 : 0 });
  const listUrl = `/funding/call-digests/?status=${status}${batch ? `&batch=${batch}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(listUrl, fetcher, { refreshInterval: generating ? 4000 : 0 });
  const digests = useMemo(() => data?.results || data || [], [data]);
  const latest = batches?.[0];

  useEffect(() => {
    const run = generating && batches?.find((b) => b.id === generating);
    if (run?.finished_at) {
      setGenerating(null);
      setToast(`Generated ${run.drafts_created} draft(s).`);
      mutate();
    }
  }, [generating, batches, mutate]);

  const refresh = () => { mutate(); mutateBatches(); };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Everyone eligible, or only the hand-picked people
  const generate = async (people) => {
    setGenerating('starting');
    try {
      const override = Boolean(people && includeEmailOff);     // never for a run for everyone
      const body = { max_calls: 5, include_email_off: override, ...(override ? { override_reason: overrideReason } : {}),
                     ...(people ? { user_ids: people.map((p) => p.id) } : {}) };
      const r = await axiosService.post('/funding/call-digests/generate/', body);
      setGenerating(r.data.batch);
      setBatch(people ? r.data.batch : '');
      mutateBatches();
      if (people) { setPicked([]); setIncludeEmailOff(false); setOverrideReason(''); }
      if (r.data.ineligible?.length) {
        setToast(`Skipped ${r.data.ineligible.map((x) => `${x.name} (${x.reason.toLowerCase()})`).join(', ')}.`);
      }
    } catch (e) {
      setGenerating(null);
      const skipped = e.response?.data?.ineligible;
      setToast(skipped?.length
        ? `${e.response.data.error} ${skipped.map((x) => `${x.name}: ${x.reason.toLowerCase()}`).join('; ')}.`
        : (e.response?.data?.error || 'Could not start generation.'));
    }
  };

  const review = async ({ decision, ids, all }) => {
    setBusy(true);
    try {
      const body = all ? { all: true, batch: batch || undefined, decision } : { ids, decision };
      const r = (await axiosService.post('/funding/call-digests/review/', body)).data;
      setToast(decision === 'approve'
        ? `Sent ${r.sent} email(s)${r.failed ? `, ${r.failed} failed` : ''}.`
        : `Rejected ${r.rejected} draft(s). Nothing was sent.`);
      setSelected([]);
      refresh();
    } catch (e) {
      setToast(e.response?.data?.error || 'Review failed.');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const onAction = (d) => async (action, callId) => {
    if (action === 'approve') { setSendReview({ ids: [d.id] }); return; }
    if (action === 'reject') { setConfirm({ decision: action, ids: [d.id], name: d.user.name }); return; }
    setBusy(true);
    try {
      if (action === 'preview') {
        const r = await axiosService.get(`/funding/call-digests/${d.id}/preview/`, { responseType: 'text' });
        setPreview(r.data);
      } else if (action === 'remove') {
        await axiosService.post(`/funding/call-digests/${d.id}/remove-item/`, { call_id: callId });
        mutate();
      } else if (action === 'regenerate') {
        await axiosService.post(`/funding/call-digests/${d.id}/regenerate/`, {}, { timeout: 120000 });
        setToast('Draft regenerated.');
        mutate();
      }
    } catch (e) {
      setToast(e.response?.data?.error || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const draftTotal = status === 'draft' ? digests.length : null;
  const confirmCount = confirm?.all ? draftTotal : confirm?.ids?.length;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        For everyone with funding-call emails on, AI picks up to 5 open funding calls (deadline at least
        10 days away, not sent to them before) and writes one sentence on why each fits. Generate for
        everyone, or hand-pick people. Nothing is emailed until you approve.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={() => generate(null)} disabled={!!generating}>
          {generating ? 'Generating…' : 'Generate for everyone'}
        </Button>
        {latest && (
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
            Last run {fmt(latest.created)}{latest.finished_at ? '' : ' (running)'}: {latest.drafts_created} drafts ·
            {latest.skipped_pending > 0 && ` ${latest.skipped_pending} skipped (a draft is already awaiting approval) ·`}
            {' '}{latest.skipped_no_match} skipped (already offered every open call)
            · {latest.tokens_used.toLocaleString()} AI tokens
          </Typography>
        )}
        {batches?.length > 1 && (
          <TextField select size="small" label="Batch" value={batch} onChange={(e) => { setBatch(e.target.value); setSelected([]); }} sx={{ minWidth: 200 }}>
            <MenuItem value="">All batches</MenuItem>
            {batches.map((b) => <MenuItem key={b.id} value={b.id}>{fmt(b.created)} ({b.drafts} drafts)</MenuItem>)}
          </TextField>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <PeoplePicker value={picked} onChange={setPicked} disabled={!!generating} includeEmailOff={includeEmailOff} />
        <Button variant="outlined" startIcon={<AutoAwesomeIcon />}
                disabled={!picked.length || !!generating || (includeEmailOff && overrideReason.trim().length < 15)}
                onClick={() => generate(picked)}>
          Generate for {picked.length || ''} {picked.length === 1 ? 'person' : 'people'}
        </Button>
      </Paper>

      {me?.is_superuser && (
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={includeEmailOff} disabled={!!generating}
                           onChange={(e) => { setIncludeEmailOff(e.target.checked); if (!e.target.checked) { setPicked((p) => p.filter((x) => x.eligible)); setOverrideReason(''); } }} />}
          label="Rare: also email the people picked above who turned off email notifications"
        />
        {includeEmailOff && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Only for a drastic update, a few people at a time (at most 25), never for everyone. These people chose not
            to receive emails: their email says it is a one-off message with a link to turn notifications on, anyone who
            switched off funding-call emails specifically is still left out, and their setting is checked again when you approve.
            <TextField fullWidth size="small" sx={{ mt: 1.5, bgcolor: 'background.paper' }} value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)} label="Why is this update drastic enough? (required, kept on record)" />
          </Alert>
        )}
      </Box>
      )}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => { if (v) { setStatus(v); setSelected([]); } }}>
          <ToggleButton value="draft">Drafts</ToggleButton>
          <ToggleButton value="sent">Sent</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
          <ToggleButton value="failed">Failed</ToggleButton>
        </ToggleButtonGroup>
        {status === 'draft' && digests.length > 0 && (
          <>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setSelected(selected.length === digests.length ? [] : digests.map((d) => d.id))}>
              {selected.length === digests.length ? 'Clear selection' : 'Select all'}
            </Button>
            <Button size="small" variant="outlined" color="success" startIcon={<SendIcon />} disabled={!selected.length || busy}
                    onClick={() => setSendReview({ ids: selected })}>
              Send selected ({selected.length})
            </Button>
            <Button size="small" variant="contained" color="success" startIcon={<DoneAllIcon />} disabled={busy}
                    onClick={() => setSendReview({ all: true })}>
              Approve &amp; send all ({digests.length})
            </Button>
            <Button size="small" color="error" disabled={!selected.length || busy}
                    onClick={() => setConfirm({ decision: 'reject', ids: selected })}>
              Reject selected
            </Button>
          </>
        )}
      </Stack>

      {error && <Alert severity="error">Failed to load drafts.</Alert>}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : !digests.length ? (
        <Alert severity="info">{status === 'draft' ? 'No drafts waiting. Generate new drafts to start.' : 'Nothing here.'}</Alert>
      ) : (
        <Stack spacing={2}>
          {digests.map((d) => (
            <DigestCard key={d.id} d={d} busy={busy} selected={selected.includes(d.id)}
                        onToggle={() => toggle(d.id)} onAction={onAction(d)} />
          ))}
        </Stack>
      )}

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>{confirm?.decision === 'approve' ? 'Approve and send?' : 'Reject drafts?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm?.decision === 'approve'
              ? `${confirm?.name ? `The email to ${confirm.name}` : `${confirmCount} email(s)`} will be sent now, and each user also gets an in-app notification. Those calls won't be offered to them again.`
              : `${confirm?.name ? `The draft for ${confirm.name}` : `${confirmCount} draft(s)`} will be discarded. Nothing is sent.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={confirm?.decision === 'approve' ? 'success' : 'error'} disabled={busy}
                  onClick={() => review(confirm)} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
            {confirm?.decision === 'approve' ? 'Send' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>Email preview</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          <iframe title="Email preview" srcDoc={preview || ''} sandbox="" style={{ width: '100%', height: '75vh', border: 0 }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>Close</Button></DialogActions>
      </Dialog>

      <EmailOutboxDialog
        open={!!sendReview}
        onClose={() => setSendReview(null)}
        title="Funding call digests"
        description="Each user gets one email with the calls picked for them, plus an in-app notification."
        sendLabel="Approve & send"
        csvName="call-digests.csv"
        loadOutbox={async () => (await axiosService.post('/funding/call-digests/outbox/',
          sendReview.all ? { all: true, batch: batch || undefined } : { ids: sendReview.ids })).data}
        loadPreview={async (email) => (await axiosService.get(`/funding/call-digests/${email.key}/preview/`, { responseType: 'text' })).data}
        onSend={async (expected) => {
          const body = sendReview.all ? { all: true, batch: batch || undefined } : { ids: sendReview.ids };
          const r = (await axiosService.post('/funding/call-digests/review/', { ...body, decision: 'approve', expected })).data;
          setToast(`Sent ${r.sent} email(s)${r.failed ? `, ${r.failed} failed` : ''}.`);
          setSendReview(null);
          setSelected([]);
          refresh();
        }}
      />

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
